import { useCallback, useEffect, useState } from "react";
import { Eye, Download, Upload, PenLine, Info, FileText } from "lucide-react";
import toast from "react-hot-toast";
import { format } from "date-fns";
import { issuedLetterService } from "../../../services/api";
import AttachmentPreviewModal from "../../Common/AttachmentPreviewModal.jsx";
import IssuedLetterSignModal from "./IssuedLetterSignModal.jsx";
import { DsButton } from "../../../design-system";

export const ISSUED_LETTER_TYPES = [
  { id: "offer", name: "Offer Letter" },
  { id: "appointment", name: "Appointment Letter" },
  { id: "transfer", name: "Transfer Letter" },
  { id: "confirmation", name: "Confirmation Letter" },
  { id: "experience", name: "Experience Letter" },
  { id: "resignation", name: "Resignation Acceptance Letter" },
  { id: "promotion", name: "Promotion Letter" },
  { id: "salary-increment", name: "Salary Increment/Revision Letter" },
  { id: "probation-extension", name: "Probation Extension Letter" },
  { id: "termination", name: "Termination Letter" },
  { id: "contract", name: "Contract Letter" },
  { id: "contract-renewal", name: "Contract Renewal/Extension Letter" },
  { id: "other", name: "Other Company Letters" },
];

function statusLabel(status) {
  if (status === "signed_through_workflow") return "Signed through workflow";
  if (status === "manually_uploaded") return "Manually uploaded";
  return "Generated";
}

const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;

function letterTypeLabel(type) {
  const match = ISSUED_LETTER_TYPES.find((t) => t.id === type);
  return match?.name || type || "Letter";
}

const INFO_TEXT =
  "Issued by Company lists letters generated from Letters Management or uploaded by HR. Status Generated is set when a letter is created from Generate Letter. Manually uploaded is set for HR uploads (single or bulk; max 10MB per PDF, stored with lossless compression). Sign (ESS) emails an OTP to the employee personal email; after verification a stamp is added and status becomes Signed through workflow.";

/**
 * Issued by Company sub-tab: list + view/download + admin upload + ESS sign.
 */
export default function IssuedByCompanyPanel({
  employeeId,
  isEmployeeView = false,
  readOnly = false,
}) {
  const [letters, setLetters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [signLetter, setSignLetter] = useState(null);
  const [previewState, setPreviewState] = useState({
    isOpen: false,
    url: null,
    downloadUrl: null,
    fileName: null,
  });

  const loadLetters = useCallback(async () => {
    if (!employeeId) return;
    setLoading(true);
    try {
      const res = await issuedLetterService.listForEmployee(employeeId);
      setLetters(Array.isArray(res?.data) ? res.data : []);
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.message || "Failed to load issued letters");
    } finally {
      setLoading(false);
    }
  }, [employeeId]);

  useEffect(() => {
    loadLetters();
  }, [loadLetters]);

  const handleView = async (letter) => {
    try {
      const [urlRes, dlRes] = await Promise.all([
        issuedLetterService.getUrl(letter.id),
        issuedLetterService.getDownloadUrl(letter.id),
      ]);
      setPreviewState({
        isOpen: true,
        url: urlRes?.url,
        downloadUrl: dlRes?.url,
        fileName: dlRes?.fileName || letter.originalFileName || `${letter.letterType}.pdf`,
      });
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to open document");
    }
  };

  const handleDownload = async (letter) => {
    try {
      const res = await issuedLetterService.getDownloadUrl(letter.id);
      if (res?.url) {
        window.open(res.url, "_blank", "noopener,noreferrer");
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to download");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <h3 className="font-semibold text-lg">Issued by Company</h3>
          <button
            type="button"
            onClick={() => setShowInfo((v) => !v)}
            className="inline-flex h-6 w-6 items-center justify-center rounded-full text-[hsl(var(--ds-muted-fg))] hover:bg-[hsl(var(--ds-muted))]"
            aria-label="Issued by Company information"
          >
            <Info size={16} strokeWidth={1.75} />
          </button>
        </div>
        {!isEmployeeView && !readOnly && (
          <DsButton type="button" onClick={() => setUploadOpen(true)}>
            <Upload className="mr-1.5" size={16} />
            Upload
          </DsButton>
        )}
      </div>

      {showInfo && (
        <div className="rounded-lg border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))]/40 p-3 text-sm text-[hsl(var(--ds-muted-fg))]">
          {INFO_TEXT}
        </div>
      )}

      <div className="overflow-hidden rounded-lg border" style={{ maxHeight: "400px", overflowY: "auto" }}>
        <table className="w-full">
          <thead className="sticky top-0 z-20 bg-[hsl(var(--ds-muted))]">
            <tr>
              <th className="px-4 py-2 text-left text-sm font-medium text-[hsl(var(--ds-muted-fg))]">
                Sr. No.
              </th>
              <th className="px-4 py-2 text-left text-sm font-medium text-[hsl(var(--ds-muted-fg))]">
                Document Type
              </th>
              <th className="px-4 py-2 text-left text-sm font-medium text-[hsl(var(--ds-muted-fg))]">
                Date
              </th>
              <th className="px-4 py-2 text-left text-sm font-medium text-[hsl(var(--ds-muted-fg))]">
                Status
              </th>
              <th className="px-4 py-2 text-left text-sm font-medium text-[hsl(var(--ds-muted-fg))]">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-center text-sm text-[hsl(var(--ds-muted-fg))]">
                  Loading…
                </td>
              </tr>
            )}
            {!loading && letters.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-center text-sm text-[hsl(var(--ds-muted-fg))]">
                  No company-issued letters yet.
                </td>
              </tr>
            )}
            {!loading &&
              letters.map((letter, index) => (
                <tr key={letter.id} className="border-t">
                  <td className="px-4 py-2 text-sm text-[hsl(var(--ds-muted-fg))]">{index + 1}</td>
                  <td className="px-4 py-2 text-sm">
                    <div className="flex items-center gap-2">
                      <FileText size={16} className="text-[hsl(var(--ds-muted-fg))]" />
                      <div>
                        <div>{letter.letterName || letterTypeLabel(letter.letterType)}</div>
                        {letter.letterName && (
                          <div className="text-xs text-[hsl(var(--ds-muted-fg))]">
                            {letterTypeLabel(letter.letterType)}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-2 text-sm text-[hsl(var(--ds-muted-fg))]">
                    {letter.generatedAt
                      ? format(new Date(letter.generatedAt), "dd/MM/yyyy")
                      : "—"}
                  </td>
                  <td className="px-4 py-2 text-sm">
                    <span
                      className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                        letter.status === "signed_through_workflow"
                          ? "bg-emerald-100 text-emerald-800"
                          : letter.status === "manually_uploaded"
                            ? "bg-sky-100 text-sky-800"
                            : "bg-amber-100 text-amber-800"
                      }`}
                    >
                      {statusLabel(letter.status)}
                    </span>
                  </td>
                  <td className="px-4 py-2">
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        title="View"
                        onClick={() => handleView(letter)}
                        className="rounded p-1 text-[hsl(var(--ds-muted-fg))] hover:bg-[hsl(var(--ds-muted))]"
                      >
                        <Eye size={16} />
                      </button>
                      <button
                        type="button"
                        title="Download"
                        onClick={() => handleDownload(letter)}
                        className="rounded p-1 text-[hsl(var(--ds-muted-fg))] hover:bg-[hsl(var(--ds-muted))]"
                      >
                        <Download size={16} />
                      </button>
                      {isEmployeeView &&
                        (letter.status === "generated" ||
                          letter.status === "manually_uploaded") && (
                        <button
                          type="button"
                          title="Sign"
                          onClick={() => setSignLetter(letter)}
                          className="rounded p-1 text-[hsl(var(--ds-primary))] hover:bg-[hsl(var(--ds-muted))]"
                        >
                          <PenLine size={16} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {uploadOpen && (
        <SingleEmployeeIssuedUploadModal
          employeeId={employeeId}
          onClose={() => setUploadOpen(false)}
          onDone={() => {
            setUploadOpen(false);
            loadLetters();
          }}
        />
      )}

      <IssuedLetterSignModal
        open={Boolean(signLetter)}
        letter={signLetter}
        onClose={() => setSignLetter(null)}
        onSigned={() => {
          setSignLetter(null);
          loadLetters();
        }}
      />

      <AttachmentPreviewModal
        isOpen={previewState.isOpen}
        onClose={() =>
          setPreviewState({ isOpen: false, url: null, downloadUrl: null, fileName: null })
        }
        url={previewState.url}
        downloadUrl={previewState.downloadUrl}
        fileName={previewState.fileName}
      />
    </div>
  );
}

function SingleEmployeeIssuedUploadModal({ employeeId, onClose, onDone }) {
  const [rows, setRows] = useState([{ file: null, letterType: "appointment" }]);
  const [busy, setBusy] = useState(false);

  const updateRow = (idx, patch) => {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };

  const handleSubmit = async () => {
    const ready = rows.filter((r) => r.file && r.letterType);
    if (!ready.length) {
      toast.error("Add at least one PDF with a letter type");
      return;
    }
    for (const r of ready) {
      if (r.file.size > MAX_UPLOAD_BYTES) {
        toast.error(`${r.file.name}: File exceeds 10MB limit`);
        return;
      }
    }
    setBusy(true);
    try {
      const form = new FormData();
      form.append("letterTypes", JSON.stringify(ready.map((r) => r.letterType)));
      ready.forEach((r) => form.append("files", r.file));
      const res = await issuedLetterService.uploadForEmployee(employeeId, form);
      const count = res?.data?.acceptedCount ?? res?.data?.accepted?.length ?? 0;
      if (count > 0) toast.success(`${count} letter(s) uploaded`);
      const rejected = res?.data?.rejected || [];
      rejected.forEach((r) => toast.error(`${r.fileName}: ${r.reason}`));
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[140] flex items-center justify-center bg-black/45 p-4">
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] p-5 shadow-xl">
        <h3 className="text-base font-semibold">Upload issued letters</h3>
        <p className="mt-1 text-xs text-[hsl(var(--ds-muted-fg))]">
          Upload one or more PDFs for this employee (max 10MB each). Status will be Manually
          uploaded. Files are compressed losslessly before storage.
        </p>
        <div className="mt-4 space-y-3">
          {rows.map((row, idx) => (
            <div
              key={idx}
              className="space-y-2 rounded-lg border border-[hsl(var(--ds-border))] p-3"
            >
              <input
                type="file"
                accept="application/pdf,.pdf"
                onChange={(e) => updateRow(idx, { file: e.target.files?.[0] || null })}
              />
              <select
                value={row.letterType}
                onChange={(e) => updateRow(idx, { letterType: e.target.value })}
                className="w-full rounded-md border border-[hsl(var(--ds-border))] bg-transparent px-2 py-1.5 text-sm"
              >
                {ISSUED_LETTER_TYPES.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </div>
          ))}
        </div>
        <button
          type="button"
          className="mt-3 text-sm text-[hsl(var(--ds-primary))]"
          onClick={() =>
            setRows((prev) => [...prev, { file: null, letterType: "appointment" }])
          }
        >
          + Add another file
        </button>
        <div className="mt-4 flex justify-end gap-2">
          <DsButton type="button" variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </DsButton>
          <DsButton type="button" onClick={handleSubmit} disabled={busy}>
            {busy ? "Uploading…" : "Upload"}
          </DsButton>
        </div>
      </div>
    </div>
  );
}
